import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { EditPolicyComponent } from 'components/edit-policy/edit-policy.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { Breadcrumb } from 'types/breadcrumb.interface';
import { ClaimDto, RoleCard, RoleCardPolicyItem, UpdateRoleRequest } from 'types/role.interface';
import { CLAIM_SCOPES, CLAIM_TYPES } from 'types/user.interface';
import { checkClaims } from 'util/user.utility';

@Component({
  selector: 'app-role-form-page',
  standalone: true,
  imports: [BreadcrumbsComponent, TranslateModule, EditPolicyComponent, SpinnerComponent],
  templateUrl: './role-form-page.component.html',
  styleUrl: './role-form-page.component.scss'
})
export class RoleFormPageComponent implements OnInit {
  public readonly CLAIM_SCOPE = CLAIM_SCOPES['role'];
  public readonly CLAIM_TYPES = CLAIM_TYPES;
  public readonly checkClaims = checkClaims;
  breadcrumbs: Breadcrumb[] = [
    {
      title: 'components.breadcrumbs.admin',
      url: ''
    },
    {
      title: 'components.breadcrumbs.roles',
      url: '/lomas'
    }
  ];
  roleId: number | null = null;
  role!: RoleCard | any;
  policies: RoleCardPolicyItem[] = [];
  isLoading: boolean = true;
  isEditable: boolean = false;

  constructor(
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.url.subscribe((segments) => {
      this.isEditable = segments.some((s) => s.path === 'rediget');
    });
    this.activatedRoute.params.subscribe((params) => {
      this.roleId = params['id'];
      if (this.roleId) {
        this.breadcrumbs.push({
          title: 'components.breadcrumbs.editRoles',
          url: `/lomas/${this.roleId}/rediget`
        });
      }

      if (this.roleId) {
        this.apiService.getRole(this.roleId).subscribe(
          (data: RoleCard) => {
            this.role = data;
            this.policies = data.policies ?? [];
            this.policies.sort((a: RoleCardPolicyItem, b: RoleCardPolicyItem) => a.name!.localeCompare(b.name!));
            this.isLoading = false;
          },
          (error: any) => {
            console.error(error);
          }
        );
      }
    });
  }

  handlePolicyChange(policy: RoleCardPolicyItem, code: string): void {
    const index = this.policies.findIndex((p: any) => p.code === code);
    if (index !== -1) {
      this.policies[index] = policy;
      this.role.policies = this.policies;
    }
  }

  submit(): void {
    this.isLoading = true;

    const role: UpdateRoleRequest = {
      id: this.role.id!,
      name: this.role.name,
      policies: this.policies.map((policy: RoleCardPolicyItem) => {
        return {
          id: policy.id,
          isEnabled: (policy as any).isEnabled,
          claims: (policy as any).claims.map((claim: ClaimDto) => {
            return {
              code: claim.code as string,
              isEnabled: (claim as any).isEnabled
            };
          })
        };
      })
    };

    this.apiService.updateRole(role).subscribe(
      (data: RoleCard) => {
        this.role = data;
        this.policies = data.policies ?? [];
        this.policies.sort((a: RoleCardPolicyItem, b: RoleCardPolicyItem) => a.name!.localeCompare(b.name!));
        this.isLoading = false;
        this.toastService.showSuccess('editRoleSuccess', '', {
          shouldTranslate: true
        });
      },
      (error: any) => {
        console.error(error);
        this.isLoading = false;
        this.toastService.showRequestError();
      }
    );
  }

  back(): void {
    this.location.back();
  }
}
